/* The events list's filter controls — STORY-040.
 *
 * ── one control set, not two ────────────────────────────────────────────────
 * The story describes a phone with one toggle and a desktop with full control,
 * and it names the failure that pairing invites: a filter set built at a desk
 * that "the phone UI cannot express", leaving somebody looking at a filtered
 * list with no way to unfilter it.
 *
 * So this renders ONE set of controls and collapses the advanced ones behind a
 * disclosure on a narrow screen. The everyday toggle stays visible; the rest is
 * one tap away rather than absent. That removes the failure instead of
 * mitigating it — there is no filter this surface cannot express, so there is
 * no link it cannot undo — and it is a `<details>`, so it costs no media query
 * and no JavaScript to decide which client is which.
 *
 * `design.md` warns that a phone-width frame proves nothing about a platform
 * whose navigation and controls are not the browser's. That is an argument for
 * prototyping natively when there is a native app, not for shipping two web
 * layouts on a guess about viewport width.
 *
 * ── the toggle is the whole phone story ─────────────────────────────────────
 * "Show completed and cancelled" is the one control most people will ever
 * touch, because the default already does the work: active events, soonest
 * first. Hiding inactive ones is not a feature — it is what you already see.
 */

import { EVENT_STATUSES, type EventStatus } from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { useId } from 'react';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button } from '../../../shared/ui/index.js';
import { DATE_RANGES, type DateRange, type EventsSearch } from '../filters.js';

const STATUS_LABELS = {
  idea: 'events.status.idea',
  planned: 'events.status.planned',
  confirmed: 'events.status.confirmed',
  completed: 'events.status.completed',
  cancelled: 'events.status.cancelled',
} as const satisfies Record<EventStatus, MessageKey>;

const RANGE_LABELS = {
  7: 'events.filters.last_7',
  30: 'events.filters.last_30',
  90: 'events.filters.last_90',
  365: 'events.filters.last_365',
} as const satisfies Record<DateRange, MessageKey>;

const SORT_LABELS = {
  date: 'events.filters.sort_date',
  name: 'events.filters.sort_name',
  status: 'events.filters.sort_status',
} as const;

/* One pair of words per sort. An explicit map rather than a constructed key —
 * `i18n.md` forbids the latter — and per-sort because "earliest first" says
 * nothing about an alphabetical order. */
const DIRECTION_LABELS = {
  date: { asc: 'events.filters.date_asc', desc: 'events.filters.date_desc' },
  name: { asc: 'events.filters.name_asc', desc: 'events.filters.name_desc' },
  status: { asc: 'events.filters.status_asc', desc: 'events.filters.status_desc' },
} as const satisfies Record<'date' | 'name' | 'status', { asc: MessageKey; desc: MessageKey }>;

/** What the default view shows, and therefore what the toggle turns on. */
const FINISHED: readonly EventStatus[] = ['completed', 'cancelled'];

export interface EventFiltersProps {
  readonly search: EventsSearch;
  readonly venues: readonly { id: string; name: string }[];
  readonly organisers: readonly { id: string; name: string }[];
  readonly onChange: (next: EventsSearch) => void;
}

export function EventFilters({ search, venues, organisers, onChange }: EventFiltersProps) {
  const t = useTranslate();
  const id = useId();

  const chosen = search.status;
  /* What is selected right now, with the default written out — every control
   * below needs it, and deriving it in three places is how they drift. */
  const current = chosen ?? EVENT_STATUSES.filter((one) => !FINISHED.includes(one));
  const showingFinished = FINISHED.every((one) => current.includes(one));

  /* Any change to a filter returns to page one. Staying on page 4 of a
   * narrower result is how somebody lands on an empty page and reads it as
   * "nothing matches" — which is a different and alarming statement. */
  const change = (next: Partial<EventsSearch>) => {
    const { page: _dropped, ...rest } = search;
    onChange({ ...rest, ...next });
  };

  const toggleStatus = (status: EventStatus) => {
    const next = current.includes(status)
      ? current.filter((one) => one !== status)
      : [...current, status];
    change({ status: next });
  };

  return (
    <div className="filters">
      <label className="filters__toggle">
        <input
          type="checkbox"
          role="switch"
          checked={showingFinished}
          aria-checked={showingFinished}
          onChange={() =>
            /* Unioned and subtracted, never replaced. Somebody who has already
               unticked "idea" and then flips this switch was asking to ADD the
               finished ones, not to have their narrowing undone — replacing
               the selection silently handed the ideas back. */
            change({
              status: showingFinished
                ? current.filter((one) => !FINISHED.includes(one))
                : [...current, ...FINISHED.filter((one) => !current.includes(one))],
            })
          }
        />
        <span>{t('events.filters.show_finished')}</span>
      </label>

      {/* Open by default on a wide screen and closed on a narrow one, by CSS
          alone — see `events.css`. A `<details>` is keyboard-operable and
          announced without a line of script, and it keeps every control
          reachable on every width. */}
      <details className="filters__more">
        <summary>{t('events.filters.more')}</summary>

        <fieldset className="filters__group">
          <legend>{t('events.filters.status_legend')}</legend>
          {EVENT_STATUSES.map((status) => (
            <label className="filters__check" key={status}>
              <input
                type="checkbox"
                checked={current.includes(status)}
                onChange={() => toggleStatus(status)}
              />
              <span>{t(STATUS_LABELS[status])}</span>
            </label>
          ))}
        </fieldset>

        <div className="field">
          <label className="field__label" htmlFor={`${id}-venue`}>
            {t('events.filters.venue')}
          </label>
          <select
            id={`${id}-venue`}
            className="field__control"
            value={search.venue ?? ''}
            onChange={(changed) =>
              change({ venue: changed.target.value === '' ? undefined : changed.target.value })
            }
          >
            <option value="">{t('events.filters.any_venue')}</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`${id}-organiser`}>
            {t('events.filters.organiser')}
          </label>
          <select
            id={`${id}-organiser`}
            className="field__control"
            value={search.organiser ?? ''}
            onChange={(changed) =>
              change({ organiser: changed.target.value === '' ? undefined : changed.target.value })
            }
          >
            <option value="">{t('events.filters.any_organiser')}</option>
            {organisers.map((organiser) => (
              <option key={organiser.id} value={organiser.id}>
                {organiser.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`${id}-within`}>
            {t('events.filters.when')}
          </label>
          {/* A relative range, never two date pickers. Nobody types dates to
              answer "what happened recently". */}
          <select
            id={`${id}-within`}
            className="field__control"
            value={search.within === undefined ? '' : String(search.within)}
            onChange={(changed) =>
              change({
                within:
                  changed.target.value === ''
                    ? undefined
                    : (Number(changed.target.value) as DateRange),
              })
            }
          >
            <option value="">{t('events.filters.any_time')}</option>
            {DATE_RANGES.map((range) => (
              <option key={range} value={String(range)}>
                {t(RANGE_LABELS[range])}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`${id}-sort`}>
            {t('events.filters.sort')}
          </label>
          <select
            id={`${id}-sort`}
            className="field__control"
            value={search.sort ?? 'date'}
            onChange={(changed) => change({ sort: changed.target.value as EventsSearch['sort'] })}
          >
            {(['date', 'name', 'status'] as const).map((sort) => (
              <option key={sort} value={sort}>
                {t(SORT_LABELS[sort])}
              </option>
            ))}
          </select>
        </div>

        {/* The direction is a button rather than a second select, and it says
            which way it is going in words — an arrow alone carries the meaning
            in colour and shape only, which `design.md` refuses.

            The words depend on WHAT is being sorted. "Earliest first" describes
            nothing when the order is by name, and this is the only control
            saying which way the list runs, so there is no other cue to fall
            back on. */}
        <Button
          variant="ghost"
          onClick={() => change({ dir: search.dir === 'desc' ? 'asc' : 'desc' })}
        >
          {t(DIRECTION_LABELS[search.sort ?? 'date'][search.dir === 'desc' ? 'desc' : 'asc'])}
        </Button>
      </details>
    </div>
  );
}
